import { useEffect, useRef, useState } from "react";
import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
} from "@/ui/attachment";
import { Badge } from "@/ui/badge";
import { Kbd } from "@/ui/kbd";
import { Progress } from "@/ui/progress";
import * as api from "../api.js";
import { ICONES, Icone, PRONTAS, dataHora, mmss } from "../visual.jsx";
import { haQuantoTempo } from "../caso.js";
import { useNavegacao } from "../navegacao.jsx";
import { useAcoes, useCelular, useEscopoEmPalavras, useEstado, useOcupado, usePode } from "../estado.jsx";
import { Botao, Faixa, MenuDeAcoes, SeloIA } from "./comuns.jsx";
import { BOTAO_DO_COMPOSER, BotaoDoComposer, Composer } from "./Composer.jsx";
import { confirmar } from "@/componentes/pergunta.tsx";
import OndaDoRecado from "./OndaDoRecado.jsx";
import { PlayerDeVoz } from "./Anexo.jsx";
import AvisoDoWhatsapp from "./AvisoDoWhatsapp.jsx";

/** A sugestão do copiloto, acima do campo de texto. */
function Sugestao() {
  const { sugestao } = useEstado();
  const ocupado = useOcupado();
  const { usarSugestao, pedirSugestao, ocultarSugestao } = useAcoes();
  return (
    /* A faixa da casa no tom da IA: era um cartão de raio 13 com geometria
       própria, o único da pilha acima da barra que não seguia as outras. */
    <Faixa
      tom="ia"
      icone={ICONES.brilho}
      classe="animate-[vFade_var(--t-curta)_var(--e-entra)_both]"
      titulo={
        <>
          Sugestão com base no caso
          {/* Não é confiança: `confianca` traz o nome do MOTOR que respondeu
              (`motor().nome`, em src/copiloto.ts) — com o OpenRouter, o id do
              modelo. Em monoespaçada e sem rótulo, lia-se como percentual. */}
          <span className="ml-2 font-mono font-normal tracking-normal text-muted-foreground normal-case">
            {sugestao?.confianca ? `por ${sugestao.confianca}` : "…"}
          </span>
        </>
      }
    >
      <div className="text-corpo leading-[1.55] text-tinta-media">
        {ocupado === "sugestao" ? "Pedindo ao assistente…" : (sugestao?.texto ?? "")}
      </div>
      {sugestao && <div className="mt-[7px] leading-[1.45] text-muted-foreground">Base: {sugestao.base}</div>}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {/* Um botão, não dois: "Usar sugestão" e "Editar antes" chamavam a MESMA
            função — o texto sempre foi para o rascunho, onde se edita antes de
            enviar. O rótulo passou a dizer o que acontece. */}
        <Botao
          rotulo="Usar no rascunho"
          aoTocar={usarSugestao}
          variante="destaque"
          tamanho="mini"
          classe="bg-verde text-sobre-terra hover:bg-verde/86"
        />
        <Botao rotulo="Outra ideia" aoTocar={() => pedirSugestao(true)} variante="fantasma" tamanho="mini" />
        <Botao rotulo="Ocultar" aoTocar={ocultarSugestao} variante="fantasma" tamanho="mini" classe="ml-auto" />
      </div>
    </Faixa>
  );
}

/**
 * O campo de escrever.
 *
 * O texto mora AQUI, não no estado global: evento que chega do servidor
 * recarrega a trilha e não encosta no que a pessoa está digitando. Quando algo
 * de fora precisa escrever (sugestão, resposta pronta), a semente muda e só
 * então o campo é recarregado.
 */
/**
 * A mensagem citada, em cima do campo — como no aplicativo.
 *
 * Fica aqui e não na bolha porque citar é estado do RASCUNHO: enquanto a barra
 * está de pé, o que sair leva a citação junto.
 */
function Citando() {
  const { respondendo, editando } = useEstado();
  const { pararDeResponder, pararDeEditar } = useAcoes();
  const alvo = editando ?? respondendo;
  if (!alvo) return null;
  const resumo = alvo.texto ?? alvo.arquivo ?? "anexo";
  const cancelar = editando ? "Cancelar edição" : "Cancelar citação";
  return (
    <Faixa
      tom={editando ? "nota" : "cita"}
      icone={editando ? ICONES.nota : ICONES.responder}
      titulo={editando ? "Editando a mensagem enviada" : `Respondendo ${alvo.direcao === "saida" ? "sua mensagem" : "o cliente"}`}
      /* ✕ mini: o de 42px (a medida da barra) fazia a citação mais alta que as
         faixas vizinhas, e o alvo de toque quem garante é o `alvo` do Botao. */
      fim={
        <Botao
          icone={ICONES.fechar}
          titulo={cancelar}
          variante="fantasma"
          tamanho="mini"
          aoTocar={editando ? pararDeEditar : pararDeResponder}
          classe="alvo"
        />
      }
    >
      <div className="truncate">{String(resumo).slice(0, 120)}</div>
    </Faixa>
  );
}

/**
 * O recado de voz: quando grava, é o composer INTEIRO.
 *
 * Antes era uma barrinha entre os ícones, e o campo de texto continuava do lado
 * piscando o cursor. Duas coisas davam errado com isso: dava para digitar
 * durante a gravação (e o Enter mandava texto no meio de um recado de voz), e o
 * cronômetro de 34px de altura, no meio de seis ícones, não parecia o estado em
 * que a mesa realmente estava — microfone aberto na sala.
 *
 * Agora é um estado do composer, como a nota interna: enquanto ele existe, o
 * campo sai de cena e todo o retângulo é a gravação. O rascunho não se perde —
 * ele mora no `Redacao`, que continua montado.
 *
 *   gravando  → onda ao vivo, cronômetro grande, descartar / pausar / enviar
 *   enviando  → a barra fica de pé até o servidor confirmar
 *   erro      → o áudio continua na mão, com "tentar de novo"
 */

/** O microfone, do lado dos outros ícones. Só aparece quando não há gravação. */
function BotaoDeMicrofone() {
  const { recado_voz, wa } = useEstado();
  const acoes = useAcoes();
  if (wa?.capacidades && !wa.capacidades.midia) return null;
  if (recado_voz) return null;
  return <BotaoDoComposer icone={ICONES.microfone} dica="Gravar recado de voz" aoTocar={acoes.comecarRecado} />;
}

/**
 * O que já está agendado para este caso, em cima do campo.
 *
 * Fica aqui e não numa aba: a mensagem que vai sair sozinha às 9h muda o que a
 * pessoa vai escrever agora. Cada linha cancela.
 */
function Agendadas() {
  const { caso } = useEstado();
  const { cancelarAgendada } = useAcoes();
  const lista = caso?.agendadas ?? [];
  if (!lista.length) return null;

  /* Uma faixa por envio, no tom da agenda: a caixa âmbar sem fio que havia
     aqui era a única da pilha com cor e geometria próprias — e âmbar, na barra,
     já quer dizer "nota interna". */
  return lista.map((a) => (
    <Faixa
      key={a.id}
      tom="agenda"
      icone={ICONES.relogio}
      titulo={<span className="tabular-nums">Agendada · {dataHora(a.quando)}</span>}
      fim={
        <Botao
          icone={ICONES.fechar}
          titulo="Cancelar este envio"
          variante="fantasma"
          tamanho="mini"
          aoTocar={() => cancelarAgendada(a.id)}
          classe="alvo"
        />
      }
    >
      <div className="truncate">{a.texto}</div>
    </Faixa>
  ));
}

/**
 * O "+" da barra: o que existe além de escrever.
 *
 * Enquete, resposta pronta e agendamento são gestos de vez em quando; ocupavam
 * a mesma fileira do clipe e do microfone, que são de sempre. Aqui eles ficam a
 * um toque, com o nome escrito — que é mais claro que três ícones de 18px.
 *
 * Quem desenha o menu é o `MenuDeAcoes` da casa, que já é o `DropdownMenu` do
 * shadcn: as respostas prontas entram como itens com marca, e o Esc, a seta e o
 * `role=menu` vêm de lá.
 */
const SEM_EXTRAS = [];

function MaisDoComposer({ texto, prontas, aoInserir, extras = SEM_EXTRAS }) {
  const { wa } = useEstado();
  const acoes = useAcoes();

  const itens = [
    ...extras,
    ...(wa?.capacidades?.enquete
      ? [
          {
            chave: "enquete",
            rotulo: "Enquete",
            detalhe: "O cliente responde tocando",
            icone: ICONES.enquete,
            aoTocar: acoes.enviarEnquete,
          },
        ]
      : []),
    {
      chave: "agendar",
      rotulo: "Agendar o envio",
      detalhe: "Sai sozinha na hora marcada",
      icone: ICONES.relogio,
      /* Agendar o que ainda não foi escrito não quer dizer nada — quem avisa é a
         própria ação, com o texto do campo na mão. */
      aoTocar: () => acoes.agendarMensagem(texto),
    },
    /* As prontas continuam com a mesma cara de antes: a marca e o texto inteiro,
       que é o que deixa escolher sem abrir uma por uma.

       A marca vai como `Kbd`: `/prim` e `/hon` são o que a pessoa DIGITA, e a
       tecla desenhada diz isso sem uma linha de legenda. Vai como elemento
       porque `marca` é filho no `MenuDeAcoes` — a casa não precisou mudar. */
    ...prontas.map((pronta) => ({
      chave: pronta.atalho,
      marca: (
        <Kbd className="h-auto min-w-0 rounded-etiqueta px-1 py-px font-mono text-selo font-bold text-terra">{pronta.atalho}</Kbd>
      ),
      detalhe: pronta.texto,
      /* O aviso de "exige aprovação" e a contagem de uso são de quem insere, e
         quem insere é o composer — o menu só aponta qual. */
      aoTocar: () => void aoInserir(pronta),
    })),
  ];

  return (
    <MenuDeAcoes
      rotulo="Mais opções de mensagem"
      titulo="Mais"
      legenda="ENQUETE, AGENDAMENTO E RESPOSTAS PRONTAS"
      /* O gatilho é o mesmo botão redondo dos outros; só o fundo muda quando o
         menu está aberto, e isso continua no `estilo` porque depende do estado. */
      classe={`${BOTAO_DO_COMPOSER} flex items-center justify-center`}
      ancora="top start"
      largura="320px"
      itens={itens}
      estilo={({ aberto }) =>
        `background:${aberto ? "color-mix(in oklab, var(--color-foreground) 8%, transparent)" : "transparent"}`
      }
    >
      <Icone de={ICONES.somar} tamanho={20} />
    </MenuDeAcoes>
  );
}

/**
 * Um botão redondo de ação da gravação — o vocabulário desse estado.
 *
 * A medida é a da barra (36 no computador, 44 no celular), e todos iguais: o
 * enviar de 46 ao lado de secundários de 40 era a mesma fileira com duas
 * alturas, e no dedo os secundários ficavam abaixo do alvo de toque. Quem
 * distingue o enviar é a cor, não o tamanho.
 */
function Redondo({ icone, rotulo, aoTocar, tom = "claro" }) {
  const pele = {
    claro: "border border-borda-controle bg-card text-tinta-media hover:bg-muted hover:text-tinta",
    perigo: "border border-terra/34 bg-terra/8 text-terra-escura hover:bg-terra/12 hover:text-terra-escura",
    forte: "border-none bg-verde text-sobre-terra hover:bg-verde/86 hover:text-sobre-terra",
  }[tom];
  return <Botao icone={icone} titulo={rotulo} aoTocar={aoTocar} tamanho="padrao" classe={`rounded-full ${pele}`} />;
}

/** O cronômetro, em monoespaçada e no tamanho de quem manda na tela (20, o
    `text-titulo`: o 19 solto não era degrau de escala nenhum). */
function Cronometro({ segundos, cor = "var(--color-terra-escura)", vivo = false }) {
  return (
    <span className="flex flex-none items-center gap-[9px]">
      {vivo && <span className="size-2.5 flex-none animate-[vPulse_1.2s_infinite] rounded-full" style={{ background: cor }} />}
      <span className="font-mono text-titulo leading-none font-medium tracking-[-.02em] tabular-nums" style={{ color: cor }}>
        {mmss(segundos ?? 0)}
      </span>
    </span>
  );
}

/** O selo que diz, em duas palavras, em que estado a mesa está. */
function Selo({ children, cor, fundo }) {
  return (
    <Badge
      className="h-auto flex-none rounded-etiqueta px-2 py-[3px] text-selo font-bold tracking-[.08em]"
      style={{ color: cor, background: fundo }}
    >
      {children}
    </Badge>
  );
}

function GravacaoNoComposer() {
  const { recado_voz: estado } = useEstado();
  const acoes = useAcoes();
  if (!estado) return null;

  /* A moldura muda de cor com o estado, e é a mesma medida do campo de texto:
     o composer não pula de altura quando a gravação começa. */
  const pele = estado.erro
    ? "border-terra/45 bg-[linear-gradient(180deg,var(--color-papel),color-mix(in_oklab,_var(--color-terra)_6%,_transparent))]"
    : estado.enviando
      ? "border-verde-fundo/34 bg-[linear-gradient(180deg,var(--color-papel),color-mix(in_oklab,_var(--color-verde)_5%,_transparent))]"
      : "border-terra/40 bg-[linear-gradient(180deg,var(--color-papel),color-mix(in_oklab,_var(--color-terra)_5%,_transparent))]";

  return (
    <div
      className={`flex min-h-[72px] animate-[vFade_var(--t-curta)_var(--e-entra)_both] flex-wrap items-center gap-3 rounded-superficie border p-[12px_14px] shadow-[0_3px_14px_color-mix(in_oklab,_var(--color-foreground)_4%,_transparent)] ${pele}`}
    >
      {estado.erro ? (
        <>
          <Selo cor="var(--color-terra-escura)" fundo="color-mix(in oklab, var(--color-terra) 12%, transparent)">
            NÃO SUBIU
          </Selo>
          <Cronometro segundos={estado.segundos} />
          <div className="min-w-[120px] flex-1 text-corpo leading-[1.45] text-terra-escura">
            {estado.erro} O áudio está aqui — não precisa gravar de novo.
          </div>
          {/* guarda-ok: pergunta em `descartarRecado` (estado.jsx), antes de soltar o microfone. */}
          <Redondo icone={ICONES.apagar} rotulo="Descartar o recado" aoTocar={acoes.descartarRecado} tom="perigo" />
          <Redondo icone={ICONES.enviar} rotulo="Tentar enviar de novo" aoTocar={() => acoes.enviarRecado()} tom="forte" />
        </>
      ) : estado.enviando ? (
        <>
          <Selo cor="var(--color-verde-fundo)" fundo="color-mix(in oklab, var(--color-verde) 12%, transparent)">
            ENVIANDO
          </Selo>
          <Cronometro segundos={estado.segundos} cor="var(--color-verde-fundo)" vivo />
          <div className="min-w-[100px] flex-1 text-corpo text-verde-fundo">Subindo o recado para o WhatsApp do cliente…</div>
        </>
      ) : (
        <>
          <Selo cor="var(--color-terra-escura)" fundo="color-mix(in oklab, var(--color-terra) 10%, transparent)">
            GRAVANDO
          </Selo>
          <Cronometro segundos={estado.segundos} vivo />
          {/* A onda toma o vão que sobra: é ela que diz que o microfone está
              ouvindo de verdade, e num vão largo dá para ver a voz mexer. */}
          <OndaDoRecado trilha={acoes.trilhaDoRecado()} quantas={34} altura={34} largura={3} folga={3} />
          {/* guarda-ok: pergunta em `descartarRecado` (estado.jsx), antes de soltar o microfone. */}
          <Redondo icone={ICONES.apagar} rotulo="Descartar sem enviar" aoTocar={acoes.descartarRecado} tom="perigo" />
          {/* Pausar PRENDE o áudio no compositor (ver `pararRecado` em
              estado.jsx): de lá se ouve com o player, escreve-se a legenda e o
              recado sai junto com a foto do documento, se for o caso. A barra de
              revisão própria — que substituía o compositor e mandava o áudio
              sozinho, sem legenda — saiu no mesmo passo.

              Só o ícone de pausa: a palavra "Ouvir" saiu porque na barra cheia
              (selo, cronômetro, onda, descartar, pausar, enviar) ela era a única
              coisa escrita, e o que ela dizia o ícone já diz. O nome continua
              existindo para quem não vê o desenho e para a dica — `aria-label`
              não é enfeite, é o nome do botão. Redondo como os vizinhos, para a
              barra deixar de ter uma peça de forma própria no meio. */}
          <Redondo
            icone={ICONES.pausar}
            rotulo="Parar e prender o áudio no compositor"
            aoTocar={() => acoes.pararRecado({ enviar: false })}
          />
          <Redondo
            icone={ICONES.enviar}
            rotulo="Enviar recado agora"
            aoTocar={() => acoes.pararRecado({ enviar: true })}
            tom="forte"
          />
        </>
      )}
    </div>
  );
}

/** Tamanho legível: 1,4 MB diz mais que 1468006. */
const tamanho = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};

/**
 * Os arquivos escolhidos e ainda não enviados, presos ao composer.
 *
 * Fica ACIMA do campo, como a citação, porque é o mesmo tipo de estado: enquanto
 * a faixa está de pé, o que sair leva isto junto. Imagem mostra miniatura — é
 * como se confere que a foto do documento é a certa antes de mandar para o
 * cliente, e conferir depois de enviar não existe.
 *
 * Cada arquivo é um `Attachment` do shadcn: mídia, nome, tamanho, ação de tirar
 * e o ESTADO do envio no mesmo lugar (`state="uploading"`), que é o que o cartão
 * feito à mão montava com quatro `<span>` posicionados.
 */
function AnexosPresos() {
  const { anexosPendentes, wa } = useEstado();
  const { tirarAnexoPendente, alternarFigurinha } = useAcoes();
  /* O botão só existe onde o provedor manda figurinha — na conta oficial da Meta
     o WebP chega como imagem comum, e oferecer seria prometer o que não sai. */
  const podeFigurinha = Boolean(wa?.capacidades?.figurinha);
  if (!anexosPendentes.length) return null;

  return (
    <div className="flex animate-[vFade_var(--t-curta)_var(--e-entra)_both] flex-wrap items-start gap-2 rounded-cartao border border-border bg-card p-[9px_10px]">
      {anexosPendentes.map((item) => {
        const imagem = item.tipo.startsWith("image/");
        const som = item.tipo.startsWith("audio/");
        const subindo = typeof item.subindo === "number";
        /*
         * O ÁUDIO preso vem com player, e não com um ícone de microfone.
         *
         * Ele chega aqui pelo botão de pausar do gravador (ver `pararRecado` em
         * estado.jsx), e o gesto inteiro existe para OUVIR antes de mandar — era
         * o que a barra de revisão dava, com o mesmo `PlayerDeVoz` da conversa.
         * Um cartão de 38px com o nome do arquivo tiraria a única razão de
         * pausar. `imediato` porque o arquivo está na mão (blob local): esperar
         * a interseção faria a onda aparecer um quadro depois.
         */
        if (som && !subindo)
          return (
            <div
              key={item.id}
              className="flex min-w-[220px] flex-1 items-center gap-2 rounded-controle border border-border bg-secondary p-[7px_9px]"
            >
              <div className="min-w-0 flex-1 text-tinta-media">
                <PlayerDeVoz anexo={{ nome: item.nome, url: item.url }} imediato />
              </div>
              <button
                type="button"
                onClick={() => tirarAnexoPendente(item.id)}
                data-dica={`Tirar ${item.nome}`}
                aria-label={`Tirar ${item.nome} do envio`}
                className="alvo flex size-[22px] flex-none cursor-pointer items-center justify-center rounded-full border-none bg-foreground/8 p-0 text-tinta-suave"
              >
                <Icone de={ICONES.fechar} tamanho={14} />
              </button>
            </div>
          );
        return (
          <Attachment
            key={item.id}
            state={subindo ? "uploading" : "done"}
            size="sm"
            className="max-w-[230px] rounded-controle border-border bg-secondary"
          >
            <AttachmentMedia
              variant={imagem ? "image" : "icon"}
              className="size-[38px] rounded-etiqueta bg-foreground/8 text-tinta-suave"
            >
              {imagem ? (
                <img src={item.url} alt="" />
              ) : (
                <Icone de={ICONES[item.tipo.startsWith("audio/") ? "microfone" : "documento"]} tamanho={20} />
              )}
            </AttachmentMedia>
            <AttachmentContent className="gap-px">
              <AttachmentTitle className="max-w-[130px] text-apoio font-semibold text-tinta-media">{item.nome}</AttachmentTitle>
              {/* Imagem pode sair como figurinha: o servidor converte para WebP
                  512 na hora do envio, e o arquivo original fica intacto na
                  pasta do cliente. */}
              {imagem && podeFigurinha ? (
                <Badge
                  render={<button type="button" />}
                  onClick={() => alternarFigurinha(item.id)}
                  aria-pressed={Boolean(item.figurinha)}
                  data-dica={item.figurinha ? "Vai como figurinha — clique para mandar como foto" : "Mandar como figurinha"}
                  className={`mt-0.5 h-auto self-start rounded-etiqueta border px-[7px] py-px text-selo font-bold tracking-[.02em] ${
                    item.figurinha
                      ? "border-verde-fundo/35 bg-verde-fundo/12 text-verde-fundo"
                      : "border-border bg-transparent text-muted-foreground"
                  }`}
                >
                  figurinha
                </Badge>
              ) : null}
              <AttachmentDescription className="font-mono text-selo text-muted-foreground">
                {tamanho(item.tamanho)}
              </AttachmentDescription>
            </AttachmentContent>
            <AttachmentActions>
              <AttachmentAction
                onClick={() => tirarAnexoPendente(item.id)}
                data-dica={`Tirar ${item.nome}`}
                aria-label={`Tirar ${item.nome} do envio`}
                className="lin size-[22px] rounded-full bg-foreground/8 text-tinta-suave hover:bg-foreground/8 hover:text-tinta-suave"
              >
                <Icone de={ICONES.fechar} tamanho={14} />
              </AttachmentAction>
            </AttachmentActions>
            {/*
             * Quanto já subiu, no próprio cartão do arquivo.
             *
             * `subindo` chega do envio (`api.anexarComProgresso`): 0 a 1 quando o
             * navegador sabe contar, -1 quando não sabe. Sem isto, o vídeo de
             * 40 MB da audiência subia sem sinal nenhum e "subindo" tinha a mesma
             * cara de "travou" — e alguém aperta Enviar de novo.
             *
             * `Progress` do shadcn: quando não há conta possível, o valor é `null`
             * e a própria primitiva marca `data-indeterminate` — é a barra cheia
             * pulsando, no lugar do gradiente que a tela desenhava à mão. A barra
             * é absoluta dentro do cartão, então a largura andando não mexe no
             * tamanho de nada — e é por isso que aqui ela pode ser largura, e não
             * o `scaleX` que a versão à mão precisava usar.
             */}
            {subindo ? (
              <Progress
                value={item.subindo < 0 ? null : Math.round(item.subindo * 100)}
                aria-label={`Enviando ${item.nome}`}
                className="pointer-events-none absolute inset-x-1.5 bottom-[3px] block gap-0 [&_[data-slot=progress-indicator]]:bg-verde [&_[data-slot=progress-track]]:h-[3px] [&_[data-slot=progress-track]]:bg-foreground/12 [&[data-indeterminate]_[data-slot=progress-indicator]]:w-full [&[data-indeterminate]_[data-slot=progress-indicator]]:animate-pulse"
              />
            ) : null}
          </Attachment>
        );
      })}
    </div>
  );
}

/**
 * As prontas do ESCRITÓRIO — com queda para as cinco que viviam na tela.
 *
 * `rapidas` só chega quando a vista de Rápidas é aberta (`carregarVista` em
 * `estado.jsx`), e a conversa é a PRIMEIRA tela de quem entra: sem a queda, a
 * fileira de atalhos e o menu do `+` ficariam vazios pela sessão inteira de quem
 * nunca passou por lá. As de `visual.jsx` são as mesmas que semearam a tabela,
 * então o texto que aparece é o certo — só sem contador de uso e sem id, que é o
 * que diz ao composer para não pedir nada ao servidor.
 */
const prontasDaCasa = (rapidas) =>
  rapidas.length ? rapidas : PRONTAS.map(([atalho, texto]) => ({ id: null, atalho, texto, usos: 0, exige_aprovacao: 0 }));

/**
 * O `/` que a pessoa está digitando, se for um comando.
 *
 * Só vale barra em começo de campo ou depois de espaço: "http://" e "22/08" têm
 * barra no meio de palavra e não são pedido de resposta pronta. Devolve onde o
 * comando começa e acaba, porque na hora de inserir ele sai junto — era o
 * comando, não o texto.
 */
const tokenDaBarra = (el) => {
  const pos = el?.selectionStart ?? 0;
  const casou = /(?:^|\s)(\/[\p{L}\d_-]*)$/u.exec((el?.value ?? "").slice(0, pos));
  return casou ? { inicio: pos - casou[1].length, fim: pos, busca: casou[1].toLowerCase() } : null;
};

/**
 * A faixa "IA no comando", DENTRO da barra de escrever.
 *
 * Ela diz o que a IA está FAZENDO (não a permissão que ela tem — isso é o
 * escopo, atrás do botão) e oferece as duas saídas no lugar em que alguém
 * decide falar: ajustar até onde ela vai, ou retomar agora.
 */
function NoComandoDaIA({ delegacao }) {
  const { envio, caso } = useEstado();
  const { retomar } = useAcoes();
  const { ajustar, palcoAberto } = useNavegacao();
  const emPalavras = useEscopoEmPalavras();
  const ultima = (caso?.mensagens ?? []).at(-1);
  /* `haQuantoTempo` da casa, e não `Date.now()` no corpo do render: relógio lido
     na pintura é função impura, e dois renders no mesmo segundo dariam textos
     diferentes sem nada ter mudado. */
  const oQueEstaFazendo =
    ultima?.direcao === "entrada"
      ? `respondendo o cliente · ${envio}`
      : ultima
        ? `a bola está com o cliente ${haQuantoTempo(ultima.quando)} — ela volta a falar se ele não responder`
        : `${emPalavras(delegacao)} · ${envio}`;

  /* A faixa da casa no tom da IA, com a frase em cima e as saídas embaixo: em
     linha, três botões ao lado da frase espremiam o "o que ela está fazendo" —
     justo a parte que faz alguém decidir retomar. */
  return (
    <Faixa tom="ia" icone={ICONES.brilho} titulo="IA no comando" classe="mb-1">
      <div className="leading-[1.35] text-tinta-media">{oQueEstaFazendo}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {/* A volta para o palco: quem fechou para ler a conversa precisa de um
            caminho de volta que não seja escrever `?palco=` na barra do navegador. */}
        {palcoAberto ? null : (
          <Botao
            rotulo="Acompanhar"
            tamanho="mini"
            aoTocar={() => ajustar({ palco: null })}
            titulo="Ver o que a IA está fazendo, passo a passo"
          />
        )}
        <Botao
          rotulo="Ajustar"
          tamanho="mini"
          aoTocar={() => ajustar({ delegar: true })}
          titulo="Até onde a IA vai sozinha neste caso"
        />
        <Botao destaque rotulo="Retomar" tamanho="mini" aoTocar={retomar} titulo="O atendimento volta para você agora" />
      </div>
    </Faixa>
  );
}

export default function Redacao() {
  const ocupado = useOcupado();
  const {
    sementeRascunho,
    sugestaoOculta,
    sugestao,
    ajustes,
    wa,
    editando,
    respondendo,
    modoComposer,
    recado_voz,
    anexosPendentes,
    rapidas,
    caso,
  } = useEstado();
  /* Quem conduz o atendimento agora: com delegação viva, a barra muda de estado
     (faixa verde, borda verde) em vez de a conversa carregar uma faixa preta. */
  const delegacao = caso?.delegacao ?? null;
  const acoes = useAcoes();
  const pode = usePode();
  const [texto, setTexto] = useState(sementeRascunho.texto);
  const campo = useRef(null);
  const digitandoAte = useRef(0);
  /* A lista do `/`: `barra` é o comando sendo digitado (ou `null`), `marcado` é
     onde a seta está. Estado local porque é gesto de digitação — não é lugar, e
     evento do servidor não pode fechá-la. */
  const [barra, setBarra] = useState(null);
  const [marcado, setMarcado] = useState(0);

  /* Sem `leads:mensagem` o campo só sabe fazer nota: o destino deixa de ser
     escolha e o rodapé passa a dizer a verdade sozinho. */
  const nota = modoComposer === "nota" || !pode("leads:mensagem");

  /*
   * "Digitando…" no celular do cliente.
   *
   * Avisa no máximo a cada 4 segundos: o WhatsApp mantém o aviso alguns segundos
   * sozinho, e mandar a cada tecla seria uma chamada por letra. Só existe onde o
   * provedor tem presença (WhatsApp Web) — nos outros a rota responde que não há,
   * sem erro.
   *
   * NOTA INTERNA NÃO AVISA NADA. Era o mesmo `onChange` para os dois modos, e o
   * cliente via "digitando…" no celular enquanto alguém escrevia, sobre ele, uma
   * anotação que ele nunca vai ler — pior, ficava esperando a mensagem que não
   * vem. A nota é o único texto da mesa que promete não sair da casa, e presença
   * é uma saída: o cliente do outro lado a vê. Vazar por ela é vazar o FATO de
   * que se está escrevendo, que já é informação sobre o caso dele.
   */
  const avisarQueDigita = (valor) => {
    if (nota) return;
    if (!wa?.capacidades?.perfil_do_contato) return;
    const agora = Date.now();
    if (!valor.trim() || agora < digitandoAte.current) return;
    digitandoAte.current = agora + 4000;
    acoes.avisarDigitando(false);
  };

  const prontas = prontasDaCasa(rapidas);
  /* Os quatro mais usados, e a ordem é a do escritório: `usos` é contado pelo
     servidor a cada inserção, então a fileira se reordena sozinha conforme o
     trabalho muda — em vez de uma lista escolhida por alguém em 2026. */
  const maisUsadas = prontas.toSorted((a, b) => (b.usos ?? 0) - (a.usos ?? 0)).slice(0, 4);
  const achadas = barra ? prontas.filter((p) => p.atalho.toLowerCase().startsWith(barra.busca)) : [];
  const listaAberta = achadas.length > 0;
  /* A lista encurta enquanto se digita: sem a trava, a seta apontaria para fora
     dela e o Enter inseriria `undefined`. */
  const naLista = Math.min(marcado, achadas.length - 1);

  /*
   * Escreve no rascunho ONDE O CURSOR ESTÁ, e nada além disso.
   *
   * O que a pessoa está digitando não se sobrescreve — é a regra da casa
   * ("recarregue a trilha, nunca o rascunho"), e ela vale também para o que a
   * própria pessoa manda inserir: quem escreveu meia frase e escolheu uma pronta
   * quer a pronta no meio dela, não em vez dela. Por isso NÃO passa por
   * `escreverNoRascunho`, que troca a semente e recarrega o campo inteiro.
   *
   * O valor vem do DOM e não do estado: entre escolher e inserir pode ter havido
   * uma pergunta de aprovação, e `texto` da renderização de origem já é passado.
   */
  const inserirNoCampo = (trecho) => {
    const el = campo.current;
    const atual = el?.value ?? texto;
    /* O `/docs` já digitado sai junto com a inserção — ele era o comando. */
    const comando = barra ? tokenDaBarra(el) : null;
    const inicio = comando?.inicio ?? el?.selectionStart ?? atual.length;
    const fim = comando?.fim ?? el?.selectionEnd ?? atual.length;
    setTexto(atual.slice(0, inicio) + trecho + atual.slice(fim));
    setBarra(null);
    const onde = inicio + trecho.length;
    /* Depois da repintura: o campo é controlado, então o cursor só existe no DOM
       quando o React já escreveu o valor novo. */
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(onde, onde);
    });
  };

  /*
   * Usar uma pronta: o servidor conta o uso e resolve `{nome}`.
   *
   * O aviso vem ANTES de `usarRapida`, e não depois: a rota incrementa `usos` e
   * resolveria o texto de uma resposta que a pessoa recusou. A flag lida aqui é
   * a da LISTA, que é a mesma que o servidor devolve — o template é que diz se
   * mexe com dinheiro, não quem manda.
   */
  const inserirPronta = async (pronta) => {
    if (!pronta) return;
    if (Number(pronta.exige_aprovacao)) {
      const ok = await confirmar({
        titulo: `Usar ${pronta.atalho}?`,
        texto:
          "Esta resposta mexe com dinheiro — chave PIX, valor de honorário. O texto entra no campo para você conferir; nada sai para o cliente sem você enviar.",
        acao: "Inserir o texto",
      });
      if (!ok) return;
    }
    /* Sem id é a queda para as prontas cravadas na tela: não há uso para contar
       nem variável para o servidor resolver. */
    const resolvido = pronta.id ? await acoes.usarRapida(pronta.id) : { texto: pronta.texto };
    if (!resolvido?.texto) return;
    inserirNoCampo(resolvido.texto);
    /*
     * A pronta pode TRAZER O ARQUIVO consigo.
     *
     * Metade das prontas do escritório é "segue o modelo de procuração" ou "ouve
     * este áudio explicando os documentos", e o texto ia sozinho: quem atendia
     * inseria a frase e depois procurava o arquivo na pasta — todas as vezes, e
     * às vezes mandava a versão velha. Agora ele entra como anexo preso, ao lado
     * do texto: confere-se antes e os dois saem no mesmo Enter.
     *
     * DEPOIS do texto e sem esperar: o texto é o que a pessoa veio inserir, e o
     * arquivo pode levar um segundo para vir do servidor — travar o campo até
     * ele chegar faria a pronta parecer lenta. Falhando, o texto continua no
     * campo e o aviso diz o que não veio: é anexo, não a mensagem.
     */
    if (pronta.id && pronta.arquivo)
      api
        .anexoDaRapida(pronta.id, pronta.arquivo_nome)
        .then((arquivo) => acoes.receberArquivos([arquivo]))
        .catch((e) => acoes.avisar(e.message));
  };

  useEffect(() => {
    setTexto(sementeRascunho.texto);
    if (sementeRascunho.texto) campo.current?.focus();
  }, [sementeRascunho]);

  /* A pílula cresce com o texto e volta a uma linha quando ele sai. É no
     código porque `field-sizing:content` só chegou no Chrome — e o campo é uma
     linha só desde que virou pílula, então sem isto um parágrafo rolava dentro
     de 40px de altura. O teto está no estilo (`max-height`). */
  useEffect(() => {
    const el = campo.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [texto]);

  /*
   * Um porteiro só para sugerir.
   *
   * Eram dois critérios para a mesma função: o cartão da sugestão olhava a
   * permissão e o botão "Sugerir" olhava o ajuste do escritório. Quem tinha
   * permissão com o ajuste desligado via o cartão sem ter como pedir; quem não
   * tinha permissão via o botão e levava recusa do servidor.
   */
  const podeSugerir = ajustes.copiloto_sugere !== "0" && pode("copiloto:sugerir");
  /*
   * No celular a barra tem espaço para o campo, o clipe, o "Mais", o microfone e
   * o envio — e nada mais. Com os cinco ícones em pé numa tela de 360px a pílula
   * ficava com 100px: dava para ver duas palavras do que se estava escrevendo.
   * Nota interna e sugestão descem para o menu "Mais", que já é o lugar do que
   * não é do minuto a minuto.
   */
  const celular = useCelular();
  /*
   * Quem só acompanha a mesa não escreve nela.
   *
   * O campo, o clipe, o microfone e o Enter apareciam para todo mundo com
   * `leads:ver`, e a recusa vinha do servidor DEPOIS de a pessoa digitar a
   * resposta inteira. A nota interna é outra permissão (`registros:anotar`,
   * a rota é `POST /api/records/:id/notes`) — quem tem uma e não a outra vê só
   * o que pode fazer.
   */
  const podeMandar = pode("leads:mensagem");
  const podeAnotar = pode("registros:anotar");
  const mostrarSugestao = !sugestaoOculta && (sugestao || ocupado === "sugestao") && podeSugerir;

  /*
   * O composer tem DESTINO, e ele se vê.
   *
   * A mesma tecla (Enter) faz duas coisas diferentes conforme o modo, então o
   * campo inteiro muda de cor: âmbar quando o que sair fica no CRM, claro quando
   * vai para o celular do cliente. Sem a moldura mudando, a única diferença
   * entre "avisar o cliente" e "anotar para a equipe" era um ícone apertado —
   * e o erro nesse par é irreversível.
   */
  /* Sem `leads:mensagem` o campo só sabe fazer nota: o destino deixa de ser
     escolha e o rodapé passa a dizer a verdade sozinho. Declarado LÁ EM CIMA
     (junto do "digitando…") porque o aviso de presença precisa dele: nota
     interna não conta como digitar para o cliente. */
  /*
   * "Não há o que mandar" e "já está saindo" são ESTADOS do envio, e não sortes.
   *
   * O círculo ficava cinza mas continuava clicável, e o Enter idem: clicar no
   * cinza chamava `enviar("")`, que voltava calado lá de dentro — a tela pedia
   * uma coisa que ela mesma sabia que não ia acontecer. Pior era o segundo
   * Enter enquanto o primeiro estava em voo: quem trava envio repetido é o
   * servidor, e ele não trava. Campo vazio COM anexo preso é envio legítimo —
   * ali o arquivo é a mensagem.
   */
  const vazio = !texto.trim() && !anexosPendentes.length;
  const saindo = ocupado === "enviando" || ocupado === "nota";
  const envioTravado = vazio || saindo;
  const despachar = () => {
    if (envioTravado) return;
    return nota ? acoes.notaInterna(texto) : acoes.enviar(texto);
  };
  /* Sem nenhuma das duas permissões não há composer: a conversa fica de leitura,
     que é exatamente o que o acesso diz. */
  const soLeitura = !podeMandar && !podeAnotar;
  /* O que o botão de enviar promete — dito na dica e no nome acessível, porque
     ele passou a ser só ícone. Com anexo preso conta quantos: "Enviar" ao lado
     de três fotos não diz se manda uma ou as três. */
  const rotuloDoEnvio = nota
    ? ocupado === "nota"
      ? "Salvando a nota…"
      : "Salvar nota interna"
    : ocupado === "enviando"
      ? "Enviando…"
      : editando
        ? "Salvar a mensagem editada"
        : anexosPendentes.length
          ? `Enviar ${anexosPendentes.length} arquivo${anexosPendentes.length > 1 ? "s" : ""}`
          : "Enviar mensagem";

  /* Gravando, o campo sai de cena inteiro. Não é só estética: com ele na tela
     dava para digitar e mandar TEXTO no meio de um recado de voz. */
  const gravando = Boolean(recado_voz);

  /* O que sai da barra no celular entra no menu — a ação é a mesma, só o lugar
     muda. Vazio no computador, onde os ícones continuam em pé. */
  const extrasDoMais = !celular
    ? []
    : [
        ...(podeAnotar && podeMandar
          ? [
              {
                chave: "nota",
                rotulo: nota ? "Voltar a escrever para o cliente" : "Escrever nota interna",
                detalhe: nota ? "O texto volta a sair para o WhatsApp" : "Fica na trilha do caso — o cliente não vê",
                icone: ICONES.nota,
                aoTocar: acoes.alternarNota,
              },
            ]
          : []),
        ...(podeSugerir
          ? [
              {
                chave: "sugerir",
                rotulo: "Pedir uma sugestão ao copiloto",
                detalhe: "Com base no caso e na conversa",
                icone: ICONES.brilho,
                aoTocar: () => acoes.pedirSugestao(false),
              },
            ]
          : []),
      ];

  if (soLeitura)
    return (
      <div className="flex-none p-[10px_18px_16px]">
        {/* Na forma da barra que ela substitui (raio 18, a mesma altura mínima)
            e apagada: é o lugar de escrever, fechado — não um aviso a mais. */}
        <div className="flex min-h-[58px] items-center gap-2 rounded-superficie border border-border bg-muted p-[11px_14px] text-corpo text-tinta-suave">
          <Icone de={ICONES.cadeado} tamanho={16} />
          Você acompanha esta conversa, mas não responde por ela. Peça acesso a quem cuida da equipe.
        </div>
      </div>
    );

  return (
    <div
      /* A faixa de gestos do aparelho entra no respiro de baixo: dentro de uma
         conversa a barra de abas não existe (ver `Casca.jsx`), então quem
         encosta no risco do "home" do iPhone é a barra de escrever. */
      className="flex-none p-[8px_10px_calc(10px+env(safe-area-inset-bottom))] sm:p-[8px_18px_calc(12px+env(safe-area-inset-bottom))]"
    >
      {/* O vão entre os blocos é do EMPILHAMENTO, não de cada bloco.
          Sugestão, citação, faixa de nota, agendadas, anexos, atalhos e a lista
          do `/` traziam cada uma a própria margem embaixo — e elas discordavam
          (9px numa, 6px nas outras), então a distância até a barra mudava
          conforme o que estivesse aberto. Um `gap` no pai vale para todos, e
          para os que ainda vão nascer. */}
      <div className="flex flex-col gap-1.5">
        {/* PRIMEIRO da pilha, acima da sugestão e da citação: é a única coisa
            aqui que muda o que acontece quando a pessoa apertar enviar. Só
            aparece com o canal fora — ver `AvisoDoWhatsapp`. */}
        <AvisoDoWhatsapp onde="composer" />
        {mostrarSugestao && <Sugestao />}
        <Citando />

        {nota && (
          <Faixa
            tom="nota"
            icone={ICONES.nota}
            titulo="Nota interna"
            classe="animate-[vFade_var(--t-curta)_var(--e-entra)_both]"
            fim={
              <Botao
                icone={ICONES.fechar}
                titulo="Voltar a escrever para o cliente"
                variante="fantasma"
                tamanho="mini"
                aoTocar={acoes.alternarNota}
                classe="alvo"
              />
            }
          >
            Fica na trilha do caso, com o seu nome. O cliente não vê.
          </Faixa>
        )}

        {gravando ? null : <Agendadas />}
        {gravando ? null : <AnexosPresos />}
        {gravando ? <GravacaoNoComposer /> : null}

        {/*
         * A fileira dos atalhos mais usados, acima do campo.
         *
         * Quatro, e não a lista inteira: é o que caberia num toque sem virar uma
         * segunda barra. O texto inteiro vai na dica — o atalho sozinho não diz o
         * que sai, e ninguém confere um texto de honorário pelo apelido dele.
         *
         * Fora do modo nota de propósito: estas frases são escritas para o
         * cliente, e a nota interna é para a equipe.
         */}
        {!gravando && !nota && maisUsadas.length ? (
          <div className="flex flex-wrap items-center gap-1.5 px-1">
            {maisUsadas.map((p) => (
              <button
                key={p.atalho}
                type="button"
                onClick={() => void inserirPronta(p)}
                data-dica={p.texto}
                aria-label={`Inserir a resposta ${p.atalho}`}
                /* Pílula mini (28) em mono terra: é o que a pessoa DIGITARIA depois
                   do `/`, e o terra diz que o texto vai para o cliente. */
                className="alvo lin inline-flex h-mini flex-none items-center rounded-full border border-borda-controle bg-card px-2.5 font-mono text-apoio font-semibold text-terra"
              >
                {p.atalho}
              </button>
            ))}
          </div>
        ) : null}

        {/*
         * A lista que o `/` abre.
         *
         * Em fluxo, empurrando o campo para baixo, como a sugestão e os anexos
         * presos já fazem: flutuante ancorada num campo que cresce com o texto
         * teria de recalcular a posição a cada linha digitada.
         *
         * O foco NÃO sai do campo — é por isso que ela não é o `Command` do
         * registro: lá o teclado é conduzido pela raiz do cmdk, que precisa do
         * foco dentro dela, e a pessoa está digitando aqui. A seta e o Enter
         * chegam pelo `onKeyDown` do campo, logo abaixo.
         */}
        {!gravando && listaAberta ? (
          <div
            id="prontas-do-composer"
            role="listbox"
            aria-label="Respostas prontas do escritório"
            className="max-h-[236px] overflow-y-auto rounded-cartao border border-border bg-card p-1 shadow-[0_8px_26px_color-mix(in_oklab,_var(--color-foreground)_12%,_transparent)]"
          >
            {achadas.map((p, i) => (
              <button
                key={p.atalho}
                type="button"
                id={`pronta-${i}`}
                role="option"
                aria-selected={i === naLista}
                /* Cancelar o mousedown é o que mantém o cursor onde estava: sem
                   isto o clique tira o foco do campo, a seleção se recolhe e o
                   texto entra no fim em vez de onde a pessoa parou. */
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void inserirPronta(p)}
                onMouseEnter={() => setMarcado(i)}
                className={`flex min-h-padrao w-full items-start gap-2.5 rounded-controle px-2.5 py-[7px] text-left ${
                  i === naLista ? "bg-foreground/8" : ""
                }`}
              >
                <Kbd className="h-auto min-w-0 flex-none rounded-etiqueta px-1 py-px font-mono text-selo leading-[17px] font-bold text-terra">
                  {p.atalho}
                </Kbd>
                <span className="line-clamp-2 min-w-0 flex-1 text-corpo leading-[1.45] text-tinta-media">{p.texto}</span>
                {/* O selo aqui é o mesmo aviso que vem depois, na pergunta: quem
                    escolhe já sabe que aquela linha mexe com dinheiro. */}
                {Number(p.exige_aprovacao) ? (
                  <Badge className="h-auto flex-none rounded-sm bg-terra/14 px-[6px] py-px text-selo font-bold text-terra-escura">
                    APROVAÇÃO
                  </Badge>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}

        {/* A BARRA DE ESCREVER é a da casa (`Composer.jsx`) — a mesma da
            conversa da peça e da dos assistentes. Aqui ela vem cheia: anexo, o
            menu do resto, nota interna, sugestão do copiloto e microfone. */}
        <Composer
          ref={campo}
          oculto={gravando}
          /*
           * COM A IA NO COMANDO, o estado mora na BARRA DE ESCREVER.
           *
           * Era uma faixa preta no alto da conversa: 44px acima das mensagens,
           * o dia inteiro, para dizer algo que só importa na hora de responder —
           * e o par de botões dela (ajustar, retomar) ficava a uma tela de
           * distância do lugar onde a pessoa decide falar. Aqui é o contrário:
           * quem vai escrever encontra o aviso e as duas saídas na mesma peça,
           * e a conversa recupera a altura.
           */
          acima={delegacao ? <NoComandoDaIA delegacao={delegacao} /> : undefined}
          tomDaBorda={delegacao ? "border-verde/55" : nota ? "border-ambar/55" : "border-borda-controle"}
          valor={texto}
          aoMudar={(valor, e) => {
            setTexto(valor);
            avisarQueDigita(valor);
            /* O `/` sendo digitado é recalculado a cada tecla — é o que abre,
               filtra e fecha a lista sem estado extra para sincronizar. */
            setBarra(tokenDaBarra(e.target));
            setMarcado(0);
          }}
          aoEnviar={despachar}
          travado={envioTravado}
          enviando={saindo}
          dicaDoEnvio={rotuloDoEnvio}
          iconeDoEnvio={ICONES[nota ? "nota" : "enviar"]}
          /* Terracota quando manda para o cliente, âmbar quando é nota: a cor
             é o que diz o DESTINO, e é por isso que a legenda saiu da barra. */
          tomDoEnvio={nota ? "nota" : "acao"}
          /* A barra dita no convite do campo, como no desenho: sem isto o
             gesto só existe para quem já sabe que ele existe. */
          convite={nota ? "Nota interna…" : celular ? "Mensagem…" : "Escreva para o cliente, ou / para uma resposta pronta"}
          rotulo={nota ? "Nota interna do caso" : "Mensagem para o cliente"}
          campoProps={{
            id: "campo-da-mensagem",
            /* A lista do `/` é uma caixa de opções ancorada NESTE campo: quem
               usa leitor de tela precisa saber que ela abriu e qual opção a
               seta está apontando, sem o foco sair de onde se digita. */
            "aria-expanded": listaAberta,
            "aria-controls": listaAberta ? "prontas-do-composer" : undefined,
            "aria-activedescendant": listaAberta ? `pronta-${naLista}` : undefined,
            /*
             * Colar print anexa o print.
             *
             * Metade do que o escritório manda é captura de tela, e o caminho era
             * salvar em disco para depois escolher no clipe. `files` da área de
             * transferência já traz a imagem — e quando não há arquivo nenhum o
             * navegador segue com a colagem normal de texto.
             */
            onPaste: (e) => {
              const arquivos = [...(e.clipboardData?.files ?? [])];
              if (!arquivos.length) return;
              e.preventDefault();
              acoes.receberArquivos(arquivos);
            },
          }}
          aoTeclar={(e) => {
            /* Com a lista aberta, as teclas de navegação são dela: a seta anda
               nas opções em vez de mover o cursor, e o Enter insere em vez de
               enviar. Fechada, nada disto existe e o campo é o de sempre. */
            if (listaAberta) {
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const passo = e.key === "ArrowDown" ? 1 : -1;
                setMarcado((naLista + passo + achadas.length) % achadas.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                void inserirPronta(achadas[naLista]);
                return;
              }
              if (e.key === "Escape") {
                /* Esc fecha a lista e para aí: a citação e a nota interna,
                   que são os próximos da fila, ficam para o Esc seguinte. */
                e.preventDefault();
                setBarra(null);
                return;
              }
            }
            /* Esc desfaz o que está pendurado no rascunho, na ordem em que
               incomoda: primeiro a edição/citação, depois a nota interna. Antes
               só o ✕ da barra fazia isso, e o Esc dentro do campo não fazia
               nada — a tecla que todo mundo aperta para cancelar. */
            if (e.key === "Escape") {
              if (editando) acoes.pararDeEditar();
              else if (respondendo) acoes.pararDeResponder();
              else if (nota) acoes.alternarNota();
            }
          }}
          /* Esquerda: o que ENTRA no texto (anexo, o menu do resto, nota, IA). */
          esquerda={
            <>
              {podeMandar && (
                <>
                  <BotaoDoComposer icone={ICONES.anexo} dica="Anexar" aoTocar={acoes.anexar} />
                  {/* O que NÃO é do minuto a minuto entra aqui: enquete, resposta
                      pronta e agendamento. */}
                  <MaisDoComposer texto={texto} prontas={prontas} aoInserir={inserirPronta} extras={extrasDoMais} />
                </>
              )}
              {/* Alterna o DESTINO do campo; quem salva é o Enter ou o botão da
                  direita. Antes este clique gravava na hora o que estivesse
                  escrito — sem confirmação, sem mostrar que era nota. */}
              {podeAnotar && podeMandar && !celular && (
                <BotaoDoComposer
                  dica={nota ? "Voltar a escrever para o cliente" : "Escrever nota interna"}
                  aoTocar={acoes.alternarNota}
                  marcado={nota}
                  fundo={nota ? "var(--color-veu-nota)" : undefined}
                >
                  <Icone de={ICONES.nota} tamanho={20} cor={nota ? "var(--color-ambar-escuro)" : undefined} />
                </BotaoDoComposer>
              )}
              {podeSugerir && !celular && (
                <BotaoDoComposer
                  dica="Pedir uma sugestão ao copiloto"
                  aoTocar={() => acoes.pedirSugestao(false)}
                  fundo="var(--color-veu-ia)"
                >
                  <SeloIA tamanho={20} />
                </BotaoDoComposer>
              )}
            </>
          }
          /* Direita: o que MANDA junto do envio. */
          direita={
            <>
              {/* O contador do WhatsApp mora aqui, e não no campo: dentro dele ele
                  empurrava o texto na horizontal a cada tecla depois de 700
                  caracteres. Aparece só quando o texto já é longo — contador
                  permanente num campo de "oi, tudo bem?" é mobília. */}
              {texto.length > 700 && (
                <span
                  /* Três tons, e só o último fala: a partir de 3.500 a
                     mensagem já é longa demais para o celular de quem lê. */
                  className={`flex-none px-1 font-mono text-selo tabular-nums ${
                    texto.length > 65_536 ? "text-terra" : texto.length > 3_500 ? "text-ambar-escuro" : "text-muted-foreground"
                  }`}
                >
                  {texto.length.toLocaleString("pt-BR")}
                  {texto.length > 65_536 ? " — o WhatsApp corta em 65.536" : ""}
                </span>
              )}
              {/* O microfone fica na ponta, ao lado do envio: é onde a mão já está
                  quando o texto acaba. */}
              {podeMandar && <BotaoDeMicrofone />}
            </>
          }
        />
      </div>
    </div>
  );
}
